// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import { ActionSheetContext, Icon, SmSpacing, Touchable } from '@alto/design-system';
import { Text } from '@alto/design-system/src/components/typography/src/Text';
import React, { useCallback, useContext } from 'react';
import { View } from 'react-native';
import { getCompanyName } from '~shared/config/getCompanyName';
import { MESSAGE_RESPONSE_TIME } from '~shared/features/messages/constants';
import { AltoAssistantActionSheet } from './AltoAssistantActionSheet';
import { MessagingActionSheet } from './MessagingActionSheet';

/**
 * Web footnote: icon is nested in Text so it stays inline after the copy (e.g. "hours.")
 * instead of being pinned to the far right by a full-width flex Row.
 *
 * Use style textAlign (not the Text `center` prop) so we get centered lines without
 * display:block stretching the row. Size to the text line (no width 100%) so
 * AnchoredFootnoteLayout can pair buttons to the same horizontal bounds.
 */
const FootnoteContent = ({
  label,
  accessibilityLabel,
  onPress,
}: {
  readonly label: string;
  readonly accessibilityLabel: string;
  readonly onPress: () => void;
}) => (
  <Touchable
    centerHorizontally
    onPress={onPress}
  >
    <Text
      textSize="mini"
      color={COLORS.TEXT_COLORS.GREY}
      // eslint-disable-next-line react-native/no-inline-styles -- web-only textAlign without center's display:block
      style={{ textAlign: 'center' }}
    >
      {label}{' '}
      <View
        // @ts-expect-error web-only CSS (inline-flex, verticalAlign) not in RN ViewStyle
        // eslint-disable-next-line react-native/no-inline-styles -- web-only inline icon alignment inside Text
        style={{ display: 'inline-flex', verticalAlign: 'text-bottom' }}
      >
        <Icon
          name="info-small"
          accessibilityLabel={accessibilityLabel}
        />
      </View>
    </Text>
  </Touchable>
);

export const SupportCaseContentFootnote = ({
  withSpacing,
  chatbotSessionStatus,
}: {
  readonly withSpacing?: boolean;
  readonly chatbotSessionStatus: string | null | undefined;
}) => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const handleIconPress = useCallback(() => {
    setActiveActionSheet(<MessagingActionSheet />);
  }, [setActiveActionSheet]);

  const handleAltoAssistantIconPress = useCallback(() => {
    setActiveActionSheet(<AltoAssistantActionSheet />);
  }, [setActiveActionSheet]);

  if (chatbotSessionStatus === 'active') {
    const companyName = getCompanyName();
    return (
      <>
        <FootnoteContent
          label={`Learn more about ${companyName} Assistant`}
          accessibilityLabel={`Press to learn more about ${companyName} Assistant`}
          onPress={handleAltoAssistantIconPress}
        />
        {withSpacing ? <SmSpacing /> : null}
      </>
    );
  }

  return (
    <>
      <FootnoteContent
        label={`We typically respond within ${MESSAGE_RESPONSE_TIME} during business hours.`}
        accessibilityLabel="Press to view why messages are not instant"
        onPress={handleIconPress}
      />
      {withSpacing ? <SmSpacing /> : null}
    </>
  );
};
